// API Key interfaces and types — aligned with asab-identity backend

// ── Key types ────────────────────────────────────────────────────────
// Scope-based keys map to predefined permission sets.
// Fine-grained keys specify individual permissions.
export type ApiKeyType = 'scope' | 'fine_grained';

// Available scopes for scope-based keys
export type ApiKeyScope = 'platform' | 'agent' | 'a2a' | 'read_only' | 'scim';

// All 23 fine-grained permissions
export type Permission =
  | 'agents:read' | 'agents:write' | 'agents:execute'
  | 'kernel:read' | 'kernel:write'
  | 'tools:callback' | 'tools:read' | 'tools:write'
  | 'orgs:read' | 'orgs:write' | 'orgs:delete'
  | 'members:read' | 'members:write'
  | 'api_keys:manage'
  | 'sso:read' | 'sso:manage'
  | 'billing:read' | 'billing:write'
  | 'models:read' | 'models:write' | 'models:execute'
  | 'executions:read'
  | 'admin';

// Scope → permissions mapping (mirrors the Lua plugin)
export const SCOPE_PERMISSIONS: Record<ApiKeyScope, string> = {
  platform: 'admin',
  agent: 'agents:read, agents:execute, tools:read',
  a2a: 'agents:execute',
  read_only: 'agents:read, orgs:read, members:read, billing:read, models:read, tools:read, executions:read',
  scim: 'members:read, members:write',
};

export const SCOPE_LABELS: Record<ApiKeyScope, string> = {
  platform: 'Platform (full access)',
  agent: 'Agent (read + execute)',
  a2a: 'A2A (execute only)',
  read_only: 'Read Only',
  scim: 'SCIM (member sync)',
};

// ── API Key model (matches backend list/get response) ────────────────
export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  key_type: ApiKeyType;
  scope?: ApiKeyScope;            // scope-based keys
  permissions?: Permission[];     // fine-grained keys
  agent_ids?: string[];           // fine-grained: restrict to specific agents
  created_by: string;
  created_at: string;
  expires_at?: string;
  rate_limit_rpm?: number;
  revoked_at?: string;
}

// ── Create request (matches backend POST body) ──────────────────────
export interface CreateApiKeyData {
  name: string;
  scope?: ApiKeyScope;            // scope-based key (exactly one of scope or permissions)
  permissions?: Permission[];     // fine-grained key
  agent_ids?: string[];
  rate_limit_rpm?: number;
  expires_in_days?: number;
}

// ── Create response (backend returns raw key once) ──────────────────
export interface CreateApiKeyResponse {
  id: string;
  key: string;                    // raw key — shown once, never stored
  masked_key: string;
  name: string;
  key_type: ApiKeyType;
  scope?: ApiKeyScope;
  permissions?: Permission[];
  created_at: string;
  expires_at?: string;
}

// ── Rotate response ─────────────────────────────────────────────────
export interface ApiKeyRotateResponse {
  id: string;
  key: string;                    // new raw key — shown once
  masked_key: string;
  name: string;
  key_type: ApiKeyType;
  created_at: string;
  expires_at?: string;
}

// ── Form data for create modal ──────────────────────────────────────
export interface CreateApiKeyFormData {
  name: string;
  key_type: ApiKeyType;
  scope?: ApiKeyScope;
  permissions?: Permission[];
  expires_in_days?: number;
  never_expires: boolean;
}

// ── Filters and pagination ──────────────────────────────────────────
export interface ApiKeyFilters {
  key_type?: ApiKeyType;
  created_after?: string;
  created_before?: string;
}

export interface ApiKeyPagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// ── API key list response (backend returns array directly) ──────────
export type ApiKeyListResponse = ApiKey[];

// ── Usage stats (from a2c/TimescaleDB, not from identity) ───────────
export interface ApiKeyUsageStats {
  total_requests: number;
  successful_requests: number;
  error_requests: number;
  unique_ips: number;
  daily_usage: Record<string, number>;
  period_days: number;
}

// ── Permission check ────────────────────────────────────────────────
export interface PermissionCheckRequest {
  resource: string;
  action: string;
  context?: Record<string, unknown>;
}

export interface PermissionCheckResponse {
  allowed: boolean;
  resource: string;
  action: string;
  reason?: string;
}

// ── State interface ─────────────────────────────────────────────────
export interface ApiKeyState {
  apiKeys: ApiKey[];
  selectedApiKey: ApiKey | null;
  isLoading: boolean;
  error: string | null;
  createKeyModalOpen: boolean;
  deleteKeyModalOpen: boolean;
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────

export function isExpired(key: ApiKey): boolean {
  if (!key.expires_at) return false;
  return new Date(key.expires_at) < new Date();
}

export function isExpiringSoon(key: ApiKey, daysThreshold = 7): boolean {
  if (!key.expires_at) return false;
  const expiresAt = new Date(key.expires_at);
  const now = new Date();
  const diff = expiresAt.getTime() - now.getTime();
  return diff > 0 && diff < daysThreshold * 24 * 60 * 60 * 1000;
}

export function getKeyStatus(key: ApiKey): 'active' | 'expired' | 'expiring_soon' {
  if (isExpired(key)) return 'expired';
  if (isExpiringSoon(key)) return 'expiring_soon';
  return 'active';
}

// Legacy compatibility — re-export for code that still references these
export type PermissionLevel = 'read' | 'write';
export type UpdateApiKeyData = { name?: string };
export type EnhancedApiKey = ApiKey;
export type CreateEnhancedApiKeyData = CreateApiKeyData;
export type UpdateEnhancedApiKeyData = UpdateApiKeyData;
export interface ApiKeyRole { id: string; name: string; }
export type CreateApiKeyRoleData = { name: string };
export type CreateEnhancedApiKeyFormData = CreateApiKeyFormData;
export type UpdateApiKeyFormData = { name: string };
export type ApiKeyWithFullKey = { api_key: ApiKey; full_key: string };
export type ApiKeyWithUsage = ApiKey & { usage?: ApiKeyUsageStats };
export type DetailedUsageStats = Record<string, unknown>;
export type BulkUsageStats = Record<string, unknown>;
export type ExpirationData = Record<string, unknown>;
// Permission catalog types (from GET /api-keys/permissions/categories)
export interface PermissionInfo {
  id: Permission;
  name: string;
  description: string;
  level: 'read' | 'write' | 'execute' | 'manage' | 'admin';
  resources: string[];
}

export interface PermissionCategory {
  id: string;
  name: string;
  description: string;
  permissions: PermissionInfo[];
}

export interface PermissionCategoriesResponse {
  categories: PermissionCategory[];
  all_permissions: Permission[];
  role_permissions: Record<string, Permission[]>;
  scope_permissions: Record<string, Permission[]>;
}
export type RateLimitInfo = Record<string, unknown>;
export type RateLimitUpdate = Record<string, unknown>;
export type ApiDocumentation = Record<string, unknown>;
export type ExportFormat = 'csv' | 'json';
